import { useEffect, useRef, useState } from "react";
import { Html5Qrcode } from "html5-qrcode";
import { X, Camera, Keyboard } from "@phosphor-icons/react";

export default function QRScanner({ onScan, onClose }) {
  const [mode, setMode] = useState("camera"); // 'camera' | 'manual'
  const [manualInput, setManualInput] = useState("");
  const [error, setError] = useState("");
  const [cameraError, setCameraError] = useState("");
  const [isSwitching, setIsSwitching] = useState(false);

  const scannerRef = useRef(null);
  const html5QrCodeRef = useRef(null);
  const hasScannedRef = useRef(false);

  useEffect(() => {
    if (mode === "camera" && !isSwitching) {
      startScanner();
    }

    return () => {
      stopScanner();
    };
  }, [mode, isSwitching]);

  const startScanner = async () => {
    setCameraError("");
    hasScannedRef.current = false;

    try {
      const html5QrCode = new Html5Qrcode("qr-reader");
      html5QrCodeRef.current = html5QrCode;

      await html5QrCode.start(
        { facingMode: "environment" },
        {
          fps: 10,
          qrbox: { width: 250, height: 250 },
          aspectRatio: 1,
        },
        async (decodedText) => {
          if (hasScannedRef.current) return;
          hasScannedRef.current = true;

          const batterySN = decodedText.trim().toUpperCase();

          await stopScanner();
          onScan(batterySN);
        },
        () => {
        },
      );
    } catch (err) {
      console.error("Camera error:", err);
      setCameraError(
        "Não foi possível acessar a câmera. Verifique as permissões ou use a entrada manual.",
      );
    }
  };

  const stopScanner = async () => {
    if (html5QrCodeRef.current) {
      try {
        await html5QrCodeRef.current.stop();
        html5QrCodeRef.current.clear();
      } catch (err) {
        console.warn("Error stopping scanner:", err);
      } finally {
        html5QrCodeRef.current = null;
      }
    }
  };

  const handleManualSubmit = (e) => {
    e.preventDefault();
    setError("");

    const batterySN = manualInput.trim().toUpperCase();
    if (!batterySN) {
      setError("Digite o código da bateria");
      return;
    }

    onScan(batterySN);
  };

  const switchMode = async (newMode) => {
    if (newMode !== mode && !isSwitching) {
      setIsSwitching(true);

      if (mode === "camera") {
        await stopScanner();
      }

      setMode(newMode);
      setError("");
      setCameraError("");
      setIsSwitching(false);
    }
  };

  const handleClose = async () => {
    if (mode === "camera") {
      await stopScanner();
    }
    onClose();
  };

  return (
    <div className="scanner-overlay fixed inset-0 z-50 bg-black/95 backdrop-blur-sm">
      <style>{`
        #qr-shaded-region div {
          border-color: #00E676 !important;
          border-width: 4px !important;
          border-radius: 8px !important;
        }
      `}</style>

      {/* Header */}
      <div className="absolute top-0 left-0 right-0 p-4 flex items-center justify-between z-10 bg-gradient-to-b from-black/80 to-transparent">
        <h2 className="text-lg font-bold text-white shadow-sm">
          Escanear Bateria
        </h2>
        <button
          onClick={handleClose}
          className="w-12 h-12 sm:w-10 sm:h-10 bg-[#1A1A1A]/80 backdrop-blur-md rounded-full flex items-center justify-center border border-white/10 hover:bg-[#252525] transition-colors"
          data-testid="close-scanner"
        >
          <X size={24} className="text-white" />
        </button>
      </div>

      {/* Mode Tabs */}
      <div className="absolute top-20 left-4 right-4 z-10 max-w-sm mx-auto">
        <div className="flex bg-[#1A1A1A]/80 backdrop-blur-md rounded-lg p-1 border border-white/10 shadow-xl">
          <button
            onClick={() => switchMode("camera")}
            disabled={isSwitching}
            className={`flex-1 py-3 px-2 sm:px-4 rounded-md flex items-center justify-center gap-2 transition-colors text-sm sm:text-base font-medium ${
              mode === "camera"
                ? "bg-[#00E676] text-black shadow-md"
                : "text-white hover:bg-white/5"
            } ${isSwitching ? "opacity-50 cursor-not-allowed" : ""}`}
            data-testid="mode-camera"
          >
            <Camera size={20} />
            Câmera
          </button>
          <button
            onClick={() => switchMode("manual")}
            disabled={isSwitching}
            className={`flex-1 py-3 px-2 sm:px-4 rounded-md flex items-center justify-center gap-2 transition-colors text-sm sm:text-base font-medium ${
              mode === "manual"
                ? "bg-[#00E676] text-black shadow-md"
                : "text-white hover:bg-white/5"
            } ${isSwitching ? "opacity-50 cursor-not-allowed" : ""}`}
            data-testid="mode-manual"
          >
            <Keyboard size={20} />
            Manual
          </button>
        </div>
      </div>

      {/* Camera Mode */}
      {mode === "camera" && (
        <div className="absolute inset-0 flex items-center justify-center pt-16">
          {cameraError ? (
            <div className="p-6 text-center max-w-sm bg-[#121212] rounded-xl border border-white/10">
              <p className="text-[#FF3B30] mb-6 text-sm">{cameraError}</p>
              <button
                onClick={() => switchMode("manual")}
                className="btn-secondary w-full py-3 font-medium"
              >
                Usar entrada manual
              </button>
            </div>
          ) : (
            <div className="relative w-full h-full flex items-center justify-center">
              <div
                id="qr-reader"
                ref={scannerRef}
                className="w-full max-w-[320px] aspect-square rounded-2xl overflow-hidden shadow-2xl"
              ></div>
            </div>
          )}
        </div>
      )}

      {/* Manual Mode */}
      {mode === "manual" && (
        <div className="absolute inset-0 flex items-center justify-center p-4">
          <form
            onSubmit={handleManualSubmit}
            className="w-full max-w-sm space-y-6 bg-[#121212] p-6 rounded-xl border border-white/10 shadow-2xl"
          >
            <div>
              <label className="text-sm text-[#A1A1AA] block mb-3 text-center font-medium">
                Digite o Código da Bateria (SN)
              </label>
              <input
                type="text"
                value={manualInput}
                onChange={(e) => setManualInput(e.target.value.toUpperCase())}
                className="input-field font-mono text-lg sm:text-xl text-center uppercase w-full h-14 tracking-widest border-[#00E676]/30 focus:border-[#00E676] bg-[#1A1A1A]"
                placeholder="EX: BAT-2024-001"
                autoFocus
                data-testid="manual-battery-input"
              />
            </div>

            {error && (
              <p className="text-[#FF3B30] text-sm text-center font-medium bg-[#FF3B30]/10 py-2 rounded-lg">
                {error}
              </p>
            )}

            <button
              type="submit"
              className="btn-primary w-full py-4 text-base font-bold shadow-lg"
              data-testid="manual-submit"
            >
              Vincular Bateria
            </button>
          </form>
        </div>
      )}

      {/* Bottom Instructions */}
      <div className="absolute bottom-8 left-4 right-4 text-center z-10">
        <div className="inline-block bg-black/60 backdrop-blur-md px-4 py-2 rounded-full border border-white/10">
          <p className="text-xs sm:text-sm text-white font-medium">
            {mode === "camera"
              ? "Posicione o QR code da bateria dentro do quadro verde"
              : "Você pode encontrar o serial na etiqueta da bateria"}
          </p>
        </div>
      </div>
    </div>
  );
}
